import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Checkbox,
  TextField,
  IconButton,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AppContext } from '../../../../context/AppContextProvider';
import ButtonComponent from '../../../../components/ButtonComponent';
import {
  useGetOnlineModeByIdAsync,
  useUpdateOnlineModeAsync,
} from '../../../../services/OnlineModeService/useMutation';

export interface INotificationWindow {
  startTime: string;
  endTime: string;
  isEnabled: boolean;
}

const OnlineModeManagementTab = () => {
  const { showSnackbar } = useContext(AppContext);
  const [isOnlineModeEnabled, setIsOnlineModeEnabled] = useState(false);
  const [schoolShiftEnabled, setSchoolShiftEnabled] = useState(false);
  const [exitByResponsibleArrival, setExitByResponsibleArrival] = useState(false);
  const [cardVerify, setCardVerify] = useState(false);
  const [resBlock, setResBlock] = useState(false);

  const [notificationWindows, setNotificationWindows] = useState<INotificationWindow[]>([]);
  const [newStartTime, setNewStartTime] = useState('07:30');
  const [newEndTime, setNewEndTime] = useState('08:30');

  const { mutateAsync: getOnlineMode } = useGetOnlineModeByIdAsync();
  const { mutateAsync: updateOnlineMode, isLoading } = useUpdateOnlineModeAsync();

  useEffect(() => {
    getOnlineMode().then((data) => {
      setIsOnlineModeEnabled(data.activeOnlineMode);
      setSchoolShiftEnabled(data.schoolShiftOn);
      setExitByResponsibleArrival(data.exitWithResponsable);
      setCardVerify(data.cardVerify);
      setResBlock(data.resBlock);
      setNotificationWindows(data.notificationWindows || []);
    });
  }, []);

  const handleAddWindow = () => {
    if (!newStartTime || !newEndTime) {
      showSnackbar('Preencha os horários de início e término.', 'error');
      return;
    }

    if (newStartTime >= newEndTime) {
      showSnackbar('O horário de início deve ser menor que o horário de término.', 'error');
      return;
    }

    const isDuplicate = notificationWindows.some(
      (w) => w.startTime === newStartTime && w.endTime === newEndTime
    );
    if (isDuplicate) {
      showSnackbar('Este intervalo de horário já foi adicionado.', 'warning');
      return;
    }

    const newWindow: INotificationWindow = {
      startTime: newStartTime,
      endTime: newEndTime,
      isEnabled: true,
    };

    setNotificationWindows((prev) => [...prev, newWindow]);
  };

  const handleRemoveWindow = (indexToRemove: number) => {
    setNotificationWindows((prev) => prev.filter((_, index: number) => index !== indexToRemove));
  };

  const handleToggleWindowEnabled = (indexToToggle: number) => {
    setNotificationWindows((prev) =>
      prev.map((window, index: number) =>
        index === indexToToggle ? { ...window, isEnabled: !window.isEnabled } : window
      )
    );
  };

  const handleSave = () => {
    updateOnlineMode({
      data: {
        activeOnlineMode: isOnlineModeEnabled,
        schoolShiftOn: schoolShiftEnabled,
        exitWithResponsable: exitByResponsibleArrival,
        cardVerify: cardVerify,
        resBlock: resBlock,
        notificationWindows: notificationWindows,
      },
    })
      .then(() => {
        showSnackbar('Configurações salvas com sucesso.', 'success');
      })
      .catch((error: any) => {
        showSnackbar(error?.response?.data?.Message || 'Falha ao salvar configurações.', 'error');
      });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configurações do Modo Online
      </Typography>

      <Typography variant="body1" sx={{ mt: 2 }}>
        Para ativar o modo online no seu dispositivo e garantir o funcionamento
        correto com nosso sistema, siga os passos abaixo:
      </Typography>

      <Typography variant="body2" sx={{ mt: 1 }}>
        1. <strong>Obtenha a referência do dispositivo:</strong> No nosso
        sistema, cada dispositivo tem um identificador único. Essa referência
        será usada para apontar corretamente os eventos de autenticação e
        notificações.
      </Typography>
      <Typography variant="body2">
        2. <strong>Ative o Modo Online:</strong> Use o botão abaixo para ativar
        o modo online no sistema.
      </Typography>
      <Typography variant="body2">
        3. <strong>Configure o Dispositivo:</strong>
        <ul style={{ marginTop: '4px' }}>
          <li>
            <strong>Endereço IP:</strong> IP do servidor onde o sistema está hospedado (ex: 10.12.14.2)
          </li>
          <li>
            <strong>Porta:</strong> Porta configurada para receber as notificações (Padrão: 5298)
          </li>
          <li>
            <strong>URL do Servidor:</strong> /notification/{'{referencia}'}
          </li>
        </ul>
      </Typography>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={isOnlineModeEnabled}
                onChange={() => setIsOnlineModeEnabled((prev) => !prev)}
              />
            }
            label="Ativar Modo Online"
          />
        </Grid>

        {/* Os checkboxes abaixo continuam dependendo do Modo Online estar ativo */}
        {isOnlineModeEnabled && (
          <>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={schoolShiftEnabled}
                    onChange={() => setSchoolShiftEnabled((prev) => !prev)}
                  />
                }
                label="Habilitar Turno Escolar"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exitByResponsibleArrival}
                    onChange={() => setExitByResponsibleArrival((prev) => !prev)}
                  />
                }
                label="Saída por chegada do responsável"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cardVerify}
                    onChange={() => setCardVerify((prev) => !prev)}
                  />
                }
                label="Saída com checagem de cartão"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resBlock}
                    onChange={() => setResBlock((prev) => !prev)}
                  />
                }
                label="Bloqueio de Responsáveis"
              />
            </Grid>
          </>
        )}

        {/* --- SEÇÃO DE REGRAS DE HORÁRIO (AGORA FORA DO IF DO MODO ONLINE) --- */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Horários Permitidos para Notificação
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Defina as janelas de horários em que o sistema enviará notificações. Se nenhuma regra for adicionada, os envios ocorrerão a qualquer momento.
          </Typography>

          {/* Formulário Inline para adicionar novo intervalo */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              label="Início"
              type="time"
              value={newStartTime}
              onChange={(e) => setNewStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              size="small"
            />
            <TextField
              label="Término"
              type="time"
              value={newEndTime}
              onChange={(e) => setNewEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              size="small"
            />
            <IconButton color="primary" onClick={handleAddWindow} title="Adicionar Horário">
              <AddCircleOutlineIcon fontSize="large" />
            </IconButton>
          </Box>

          {/* Listagem Estilizada para Tema Escuro */}
          {notificationWindows.length > 0 ? (
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0, mt: 2, maxWidth: 500 }}>
              {notificationWindows.map((window, index: number) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveWindow(index)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  }
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    py: 1,
                    px: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={window.isEnabled}
                        onChange={() => handleToggleWindowEnabled(index)}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 1,
                          fontWeight: window.isEnabled ? 'medium' : 'normal',
                          color: window.isEnabled ? 'text.primary' : 'text.disabled',
                        }}
                      >
                        Das <strong style={{ color: '#2196f3' }}>{window.startTime}</strong> às <strong style={{ color: '#2196f3' }}>{window.endTime}</strong>
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ fontStyle: 'italic', mt: 1 }}
            >
              Nenhuma janela de horário configurada. (Disparos liberados 24h).
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <ButtonComponent onClick={handleSave} loading={isLoading}>
            Salvar
          </ButtonComponent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OnlineModeManagementTab;